import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Check } from "lucide-react";

const PaymentStep = ({ data, updateData, onNext }) => {
  const [selectedPlan, setSelectedPlan] = useState("1-year");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(600); // 10-minute countdown

  useEffect(() => {
    // Countdown Timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Fetch plans from API
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          "https://api.butterscotchai.app/config/appinfo"
        );
        const data = await res.json();
        const plansData = data.config.config.paymentPlans.iap.products;

        plansData.sort((a, b) => a.price - b.price);

        const filteredPlans = plansData
          .filter((plan) => plan.private === true)
          .sort((a, b) => b.price - a.price); // Highest price first

        plansData.length = 0;
        plansData.push(...filteredPlans);

        const special = plansData[0];
        setSelectedPlan(special.productId);

        setPlans(plansData);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleContinue = (productId) => {
    updateData("selectedPlan", productId);

    const jsonString = JSON.stringify(data);
    const base64 = btoa(jsonString);
    const encodedData = encodeURIComponent(base64);

    function getCookie(name) {
      if (typeof document === "undefined") return null; // SSR safety
      const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
      return m ? decodeURIComponent(m[2]) : null;
    }

    // usage
    const fbp = encodeURIComponent(getCookie("_fbp"));
    const fbc = encodeURIComponent(getCookie("_fbc"));

    // const redirectUrl = "https://signup.butterscotchai.app/onboarding/success";
    // const encodedRedirectUrl = encodeURIComponent(redirectUrl);

    window.location.href = `https://vampire-indol.vercel.app/purchase/completePayment?productId=${productId}&userId=anonymous&fbc=${fbc}&fbp=${fbp}&data=${encodedData}`;
    // onNext();
  };
  if (loading) {
    return (
      <div className="text-center text-white py-20">
        <div className="animate-spin border-4 border-t-transparent border-white/40 rounded-full w-10 h-10 mx-auto mb-4"></div>
        Loading plans...
      </div>
    );
  }

  return (
    <div className="text-center px-4 py-6 max-h-screen overflow-y-auto">
      {/* Hero Image */}
      <div className="mb-6">
        <div className="w-full max-w-sm mx-auto h-[65vh] rounded-3xl overflow-hidden bg-gradient-to-br from-[#F1B428]/20 to-[#1A1A1A] border border-white/10 flex items-center justify-center">
          <video
            src="https://exh-smm.s3.us-west-2.amazonaws.com/IMG_2252_fn.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full max-w-md rounded-xl shadow-lg"
          ></video>
        </div>
      </div>

      <h1 className="text-white text-2xl md:text-3xl font-bold mb-4">
        Your AI GF is ready 🔥
      </h1>

      {/* Countdown Timer */}
      <div className="bg-gradient-to-r from-[#FF69B4] to-[#FF1493] text-white font-bold py-3 px-6 rounded-full inline-block mb-6">
        TIME-LIMITED OFFER {formatTime(timeLeft)}
      </div>

      <p className="text-white/70 text-sm mb-6">
        get your personal 30% discount for a complete
        <br />
        immersion into the world of Butterscotch AI
      </p>

      {/* Fake email badges */}
      <div className="flex gap-2 justify-center mb-6 flex-wrap">
        <div className="bg-[#6C5CE7] text-white px-4 py-2 rounded-lg text-xs">
          🔥 Spicy
        </div>
        <div className="bg-[#6C5CE7] text-white px-4 py-2 rounded-lg text-xs">
          💖 Loving
        </div>
        <div className="bg-[#6C5CE7] text-white px-4 py-2 rounded-lg text-xs">
          ⚡ Hot
        </div>
      </div>

      {/* Pricing Plans */}
      <div className="space-y-4 mb-6 max-w-md mx-auto">
        {plans.map((plan, index) => (
          <button
            key={plan.productId}
            onClick={() => setSelectedPlan(plan.productId)}
            className={`w-full text-left rounded-3xl p-6 transition-all duration-300 relative ${
              selectedPlan === plan.productId
                ? "bg-gradient-to-br from-[#FF69B4]/20 to-[#FF1493]/20 border-2 border-[#FF69B4] scale-105"
                : "bg-white/5 border border-white/10 hover:border-white/30"
            }`}
          >
            {plan.isSpecial && (
              <div className="absolute -top-3 right-6 bg-gradient-to-r from-[#FF69B4] to-[#FF1493] text-white px-4 py-1 rounded-full text-xs font-bold">
                Most Popular
              </div>
            )}

            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-white font-bold text-lg mb-1">
                  Generate your girlfriend
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-white/50 line-through text-sm">
                    ₹{plan.price * 2}
                  </span>
                  <span className="text-white font-bold text-xl">
                    ₹{plan.price}
                  </span>
                  <span className="text-white/70 text-sm">one time</span>
                </div>
              </div>
              {/* <div className="bg-[#6C5CE7] text-white px-3 py-2 rounded-lg">
                <div className="text-2xl font-bold">$2.12</div>
                <div className="text-xs">per day</div>
              </div> */}
            </div>

            {[
              "Control Memory",
              "Role-playing",
              "Hot Photos & Videos",
              "Unlimited messages",
              "2x energy",
            ]?.length > 0 ? (
              <div className="space-y-2 mb-4">
                {(index === 2
                  ? ["Generate your Girlfriend", `${plan.credits} Free credits`]
                  : index === 1
                  ? [
                      "Generate your Girlfriend",
                      `${plan.credits} Free credits`,
                      "Unlimited messages",
                      "Spicy Photos & Videos",
                    ]
                  : [
                      "Generate your Girlfriend",
                      `${plan.credits} Free credits`,
                      "Unlimited messages",
                      "Spicy Photos & Videos",
                      "Live 18+ Story updates",
                    ]
                ).map((feature, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-white/80 text-sm"
                  >
                    <span>{["💖", "💰", "💬", "🔥", "🔞"][idx]}</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            ) : (
              <button className="text-white/50 text-sm flex items-center gap-1 hover:text-white/70">
                Learn more <span>↓</span>
              </button>
            )}

            {[
              "Control Memory",
              "Role-playing",
              "Hot Photos & Videos",
              "Unlimited messages",
              "2x energy",
            ]?.length > 0 && (
              <Button
                onClick={() =>
                  // setSelectedPlan(plan.productId)
                  handleContinue(plan.productId)
                }
                className="w-full bg-gradient-to-r from-[#FF69B4] to-[#FF1493] hover:from-[#FF1493] hover:to-[#FF69B4] text-white font-bold py-3 rounded-full"
              >
                Get Your Plan
              </Button>
            )}
          </button>
        ))}
      </div>

      <Button
        onClick={() => handleContinue(selectedPlan)}
        className="w-full max-w-md bg-[#F1B428] hover:bg-[#D9A022] text-black font-bold text-lg py-6 rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-[#F1B428]/30"
      >
        Continue
      </Button>
    </div>
  );
};

export default PaymentStep;
